/*
 * Состояние связи.
 *
 * Одно место, которое знает, почему приложение молчит: телефон не в сети,
 * сервер не отвечает или отвечает слишком долго. Раньше об этом знал каждый
 * экран по-своему — и человек видел то пустую страницу, то чужой экран входа
 * с красной строкой, хотя из аккаунта никто не выходил.
 *
 * Про VPN браузер сказать не может: сетевые интерфейсы видит только родное
 * приложение. Нам доступны ровно два факта — что говорит `navigator.onLine` и
 * доходят ли запросы до нашего сервера. Догадку за знание не выдаём: экран
 * про VPN лишь предполагает причину.
 */

export type NetworkState =
	/** Связь есть и работает. */
	| 'ok'
	/** Устройство не в сети — это браузер знает точно. */
	| 'offline'
	/** Сеть у устройства есть, а до нашего сервера запросы не доходят. */
	| 'unreachable'
	/** Ответы приходят, но слишком долго. */
	| 'slow';

/**
 * Дольше этого ответ считается медленным.
 *
 * Четыре секунды — это уже «приложение висит» на глаз, но ещё не обрыв:
 * запрос дойдёт, просто человек успеет решить, что всё сломалось.
 */
const SLOW_MS = 4_000;

/** Поле есть не везде — Safari о нём не знает, и это не повод от него отказываться. */
interface NetworkInformation {
	effectiveType?: string;
	addEventListener?: (type: string, listener: () => void) => void;
	removeEventListener?: (type: string, listener: () => void) => void;
}

function connection(): NetworkInformation | undefined {
	if (typeof navigator === 'undefined') return undefined;
	return (navigator as Navigator & { connection?: NetworkInformation }).connection;
}

function onlineNow(): boolean {
	// На сервере при сборке страниц навигатора нет, и связь там ни при чём.
	return typeof navigator === 'undefined' ? true : navigator.onLine;
}

class NetworkStatus {
	private online = $state(onlineNow());
	/** Дошёл ли до сервера последний запрос. */
	private reachable = $state(true);
	private lastDurationMs = $state(0);
	private effectiveType = $state(connection()?.effectiveType ?? '');

	state: NetworkState = $derived.by(() => {
		if (!this.online) return 'offline';
		if (!this.reachable) return 'unreachable';
		if (this.lastDurationMs > SLOW_MS) return 'slow';
		if (this.effectiveType === '2g' || this.effectiveType === 'slow-2g') return 'slow';
		return 'ok';
	});

	/** Связи нет вовсе — приложению нечего показывать, кроме объяснения. */
	down = $derived(this.state === 'offline' || this.state === 'unreachable');

	/** Запрос дошёл. Заодно запоминаем, насколько медленно. */
	reportSuccess(durationMs: number): void {
		this.reachable = true;
		this.lastDurationMs = durationMs;
	}

	/**
	 * Запрос не дошёл до сервера вовсе.
	 *
	 * Только про сетевой сбой: отказ сервера (401, 500) сюда не попадает —
	 * связь при нём как раз работает.
	 */
	reportFailure(): void {
		this.reachable = false;
	}

	/**
	 * Слушать браузер. Возвращает отписку.
	 *
	 * `navigator.onLine` меняется мгновенно и без запросов — это самый быстрый
	 * и самый достоверный из наших признаков.
	 */
	watch(): () => void {
		if (typeof window === 'undefined') return () => undefined;

		const sync = () => {
			this.online = onlineNow();
			// Вернулась сеть — прошлый отказ больше ничего не значит: пусть
			// решает следующий запрос, а не память о старом сбое.
			if (this.online) this.reachable = true;
		};
		const syncType = () => (this.effectiveType = connection()?.effectiveType ?? '');

		window.addEventListener('online', sync);
		window.addEventListener('offline', sync);
		connection()?.addEventListener?.('change', syncType);

		return () => {
			window.removeEventListener('online', sync);
			window.removeEventListener('offline', sync);
			connection()?.removeEventListener?.('change', syncType);
		};
	}
}

export const network = new NetworkStatus();
